const GUTTER = 12;
const OFFSET = 16;
/** A dragged bubble must keep at least this much of itself on screen. */
const MIN_VISIBLE = 40;

export type BubbleBox = {
  left: number;
  top: number;
  /** transform-origin pointing back at the pin, e.g. "left 20px". */
  origin: string;
};

/**
 * Place a bubble next to a pin (viewport coordinates), preferring the right
 * side and flipping/clamping so it never clips outside the viewport.
 */
export function placeBubble(
  pinX: number,
  pinY: number,
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
): BubbleBox {
  let left = pinX + OFFSET;
  let originX = "left";
  let fitsBeside = true;
  if (left + width > viewportWidth - GUTTER) {
    const flipped = pinX - OFFSET - width;
    if (flipped >= GUTTER) {
      left = flipped;
      originX = "right";
    } else {
      // Neither side fits (narrow viewport): center it and go below/above the
      // pin so the bubble never covers the pin itself.
      left = Math.max(
        GUTTER,
        Math.min((viewportWidth - width) / 2, viewportWidth - width - GUTTER),
      );
      fitsBeside = false;
    }
  }

  if (!fitsBeside) {
    let top = pinY + OFFSET;
    let originYEdge = "top";
    if (top + height > viewportHeight - GUTTER) {
      top = Math.max(GUTTER, pinY - OFFSET - height);
      originYEdge = "bottom";
    }
    const originXPx = Math.max(0, Math.min(pinX - left, width));
    return { left, top, origin: `${originXPx}px ${originYEdge}` };
  }

  const preferredTop = pinY - 20;
  const top = Math.max(GUTTER, Math.min(preferredTop, viewportHeight - height - GUTTER));
  const originY = Math.max(0, Math.min(pinY - top, height));

  return { left, top, origin: `${originX} ${originY}px` };
}

/** Clamp a dragged bubble position so a usable sliver always stays visible. */
export function clampDraggedPosition(
  left: number,
  top: number,
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
): { left: number; top: number } {
  return {
    left: Math.min(Math.max(left, MIN_VISIBLE - width), viewportWidth - MIN_VISIBLE),
    top: Math.min(Math.max(top, 0), viewportHeight - MIN_VISIBLE),
  };
}
